import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { CalendarPlus } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { LocationsSection } from '@/components/sections/locations-section'

export default function ClinicsPage() {
  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  const handleBookLocation = (locationName: string) => {
    const source = locationName === 'Noosa' ? 'noosa_clinic' : 'maroochydore_clinic'
    openBooking(source)
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Clinics | Raintree Chinese Medicine</title>
        <meta name="description" content="Raintree Chinese Medicine offers appointments at two clinic locations on the Sunshine Coast: Noosa and Maroochydore. Choose whichever is easiest for you to reach." />
        <meta property="og:title" content="Clinics | Raintree Chinese Medicine" />
        <meta property="og:description" content="Raintree Chinese Medicine offers appointments at two clinic locations on the Sunshine Coast: Noosa and Maroochydore. Choose whichever is easiest for you to reach." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/clinics" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Clinics | Raintree Chinese Medicine" />
        <meta name="twitter:description" content="Raintree Chinese Medicine offers appointments at two clinic locations on the Sunshine Coast: Noosa and Maroochydore. Choose whichever is easiest for you to reach." />
      </Helmet>
      <h1 className="sr-only">Clinics</h1>
      <BreadcrumbsJsonLd />
      
      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                Clinics
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Raintree Chinese Medicine currently offers appointments at two clinic locations on the Sunshine Coast. You're welcome to choose whichever is easiest for you to reach.
              </p>
              <div className="flex flex-col gap-3 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
              </div>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  Clinics
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                  Raintree Chinese Medicine currently offers appointments at two clinic locations on the Sunshine Coast. You're welcome to choose whichever is easiest for you to reach.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Clinic Locations Section */}
      <LocationsSection onBook={handleBookLocation} />

    </main>
  )
}

